import { useEffect, useState } from "react";
import { X, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/** Inline WhatsApp brand mark, single-colour path. */
function WhatsAppIcon({ size = 32, className = "" }) {
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.03 1.375-1.045 2.32v.13c-.014.99.472 1.977 1.017 2.9 1.23 2.035 2.9 3.72 5.02 4.774.68.338 2.293 1.04 3.055 1.04.514 0 2.53-.73 2.53-1.687 0-.13-.028-.244-.056-.372-.16-.71-.615-.914-1.216-1.16-.302-.115-.573-.343-.858-.343zM15.99 26.72c-1.99 0-3.923-.577-5.57-1.61l-3.895 1.02 1.04-3.795A10.36 10.36 0 0 1 5.65 16.72c0-5.708 4.63-10.338 10.34-10.338s10.34 4.63 10.34 10.338c0 5.71-4.63 10.34-10.34 10.34zm0-22.72C9.03 4 3.36 9.67 3.36 16.72c0 2.243.586 4.442 1.72 6.376L3.03 30l7.05-1.847a12.66 12.66 0 0 0 5.91 1.5c6.96 0 12.63-5.67 12.63-12.63 0-3.373-1.312-6.545-3.7-8.928A12.545 12.545 0 0 0 15.99 4z"
      />
    </svg>
  );
}

/** Filled iOS-style phone glyph. */
function PhoneFilledIcon({ size = 32, className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2c.27-.27.68-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1A17 17 0 0 1 3 5c0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.24.2 2.45.57 3.57.11.35.03.75-.25 1.02l-2.2 2.2z"
      />
    </svg>
  );
}

/**
 * Reusable contact picker modal. Two full-width horizontal tiles:
 *   1. Phone   — dark navy `#062843`, iOS-style filled phone glyph
 *   2. WhatsApp — brand green `#25D366`, WhatsApp glyph
 *
 * Phone renders ON TOP, WhatsApp BELOW (explicit ordering). Either tile
 * is hidden when its corresponding public setting is null. If both are
 * null the modal auto-closes and surfaces a toast.
 *
 * Props:
 *   open      boolean
 *   onClose   () => void
 *   settings  optional pre-fetched public settings
 *   title     modal heading, default "Get in touch"
 *   subtitle  optional lead-in copy under the heading
 */
export default function ContactPickerModal({
  open,
  onClose,
  settings: propSettings,
  title = "Get in touch",
  subtitle,
}) {
  const [settings, setSettings] = useState(propSettings ?? null);

  useEffect(() => {
    if (propSettings) {
      setSettings(propSettings);
      return;
    }
    if (!open) return;
    let ignore = false;
    api
      .get("/settings/public")
      .then((r) => !ignore && setSettings(r.data))
      .catch(() => !ignore && setSettings({}));
    return () => {
      ignore = true;
    };
  }, [open, propSettings]);

  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !settings) return;
    if (!settings.public_phone && !settings.public_whatsapp) {
      toast.info("Contact info coming soon.");
      onClose();
    }
  }, [open, settings, onClose]);

  if (!open) return null;

  const phone = settings?.public_phone || null;
  const whatsapp = settings?.public_whatsapp || null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] flex items-center justify-center p-5"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-picker-title"
      data-testid="contact-picker-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 md:p-7 relative hbe-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-2 text-hbe-ink/45 hover:text-hbe-primary"
          aria-label="Close"
          data-testid="contact-picker-close"
        >
          <X size={20} />
        </button>

        <h2
          id="contact-picker-title"
          className="font-serif text-2xl md:text-3xl text-hbe-primary pr-8"
        >
          {title}
        </h2>
        {subtitle && (
          <p className="mt-2 text-sm text-hbe-ink/65">{subtitle}</p>
        )}

        <div className="mt-6 flex flex-col gap-3">
          {/* Phone — navy, ON TOP */}
          {phone && (
            <a
              href={`tel:${phone.replace(/[^+\d]/g, "")}`}
              className="group flex items-center gap-4 rounded-2xl py-4 px-5 text-white transition"
              style={{ background: "#062843" }}
              data-testid="contact-picker-phone"
              onClick={onClose}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#04203a")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#062843")}
            >
              <span className="inline-flex items-center justify-center w-10 h-10 shrink-0">
                <PhoneFilledIcon size={30} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold leading-tight">
                  Call
                </div>
                <div className="mt-0.5 text-sm text-white/80 break-all">
                  {phone}
                </div>
              </div>
              <ChevronRight
                size={18}
                className="text-white/80 group-hover:translate-x-0.5 transition-transform shrink-0"
              />
            </a>
          )}
          {/* WhatsApp — brand green, BELOW */}
          {whatsapp && (
            <a
              href={`https://wa.me/${whatsapp.replace(/[^\d]/g, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 rounded-2xl py-4 px-5 text-white transition"
              style={{ background: "#25D366" }}
              data-testid="contact-picker-whatsapp"
              onClick={onClose}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#128C7E")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#25D366")}
            >
              <span className="inline-flex items-center justify-center w-10 h-10 shrink-0">
                <WhatsAppIcon size={30} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold leading-tight">
                  WhatsApp
                </div>
                <div className="mt-0.5 text-sm text-white/85 break-all">
                  {whatsapp}
                </div>
              </div>
              <ChevronRight
                size={18}
                className="text-white/85 group-hover:translate-x-0.5 transition-transform shrink-0"
              />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/** Small hook so a parent trigger button can be disabled when no contact
 *  fields are set on public settings. */
export function useHasPublicContact() {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    let ignore = false;
    api
      .get("/settings/public")
      .then((r) => !ignore && setSettings(r.data))
      .catch(() => !ignore && setSettings({}));
    return () => {
      ignore = true;
    };
  }, []);
  return {
    loading: settings === null,
    hasAny: Boolean(settings?.public_phone || settings?.public_whatsapp),
    settings,
  };
}
